import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import qs from 'qs';
import { QueryKey } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { QueryKeys } from './api/queryKeys';
import { getAccessToken } from './auth';
import {
  checkVersion,
  setConnectionFailed,
  setConnectionState,
} from './features/app/appStatus';
import { applyDowntimePush } from './features/downtime/queryHooks';
import {
  invalidateCellBoardMessages,
  invalidateMCellMessages,
} from './features/cellBoard/queryHooks';
import { setEmployeeRefreshRunning } from './features/mCellEmployee/queryHooks';

const SIGNAL_R_ROOT_PATH = '/hubs/ccc';
const FAILURE_THRESHOLD = 3; // Show alert after 3 failures
const RECONNECT_DELAYS_MS = [
  0,
  2000, // 2 secs
  2000,
  2000,
  10000, // 10 secs
  10000,
  10000,
  30000, // 30 secs
  30000,
  30000,
  60000, // 1 min
  60000,
  60000,
  3600000, // 1 hr
  3600000,
  3600000,
  86400000, // 24 hrs
];

const invalidate = (...queryKeys: QueryKey[]) =>
  queryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

/**
 * Subscribes to the hub's pushes. Pushes mark the affected queries stale so they refetch from REST,
 * which stays the source of truth. Pushes that carry the full state are written to the cache instead.
 * @param connection
 */
const handlePushes = (connection: HubConnection) => {
  connection.on('Application', (messageType: string, payload: string) => {
    if (messageType === 'Version') {
      checkVersion(payload);
    }
  });

  connection.on('Downtime', (messageType: string, payload: any) =>
    applyDowntimePush(queryClient, messageType, payload),
  );

  connection.on('DowntimeReason', (messageType: string) =>
    invalidate(
      messageType.startsWith('DowntimeReasonSupportGroupMapping')
        ? [QueryKeys.DOWNTIME_REASON_SUPPORT_GROUP_MAPPINGS]
        : [QueryKeys.DOWNTIME_REASONS],
    ),
  );

  connection.on('ManufacturingLocation', () =>
    invalidate([QueryKeys.MCELL_COLLECTIONS]),
  );

  connection.on('CommandCenter', (messageType: string, payload: any) =>
    messageType === 'DashboardDeleted'
      ? // Refetching the deleted dashboard would 404
        invalidate([QueryKeys.DASHBOARDS])
      : invalidate([QueryKeys.DASHBOARDS], [QueryKeys.DASHBOARD, payload.id]),
  );

  connection.on('CellBoard', (messageType: string) => {
    if (messageType === 'CellBoardMCellMessageDeleted') {
      // Refetching the deleted message would fail
      invalidate([QueryKeys.MCELL_MESSAGES, 'list']);
    } else if (messageType.startsWith('CellBoardMCellMessage')) {
      invalidateMCellMessages(queryClient);
    } else if (messageType === 'CellBoardMessageDeleted') {
      // Refetching the deleted message would 404
      invalidate([QueryKeys.CELL_BOARD_MESSAGES, 'list']);
    } else {
      invalidateCellBoardMessages(queryClient);
    }
  });

  connection.on('BreakDisplay', () => invalidate([QueryKeys.BREAK_DISPLAY]));

  connection.on('SupportHierarchy', (messageType: string) => {
    if (messageType.startsWith('SupportGroupDowntimeEscalationPosition')) {
      invalidate([QueryKeys.DOWNTIME_ESCALATION_POSITION_OVERRIDES]);
    } else if (messageType.startsWith('SupportGroupDowntimeEscalation')) {
      invalidate([QueryKeys.DOWNTIME_ESCALATION_OVERRIDES]);
    } else if (messageType.startsWith('SupportGroup')) {
      invalidate([QueryKeys.SUPPORT_GROUPS]);
    } else {
      invalidate(
        [QueryKeys.SUPPORT_HIERARCHIES],
        [QueryKeys.SUPPORT_HIERARCHY_MCELLS],
        [QueryKeys.MCELL_SUPPORT_HIERARCHY],
      );
    }
  });

  connection.on('SchedulingTool', (messageType: string, signal: boolean) => {
    if (messageType === 'RefreshInitiated') {
      queryClient.setQueriesData<boolean>(
        { queryKey: [QueryKeys.ST_REFRESH_RUNNING] },
        signal,
      );
    }
  });

  connection.on('Employee', (messageType: string, signal: boolean) => {
    if (messageType === 'RefreshEmployeesInitiated') {
      setEmployeeRefreshRunning(queryClient, signal);
    }
  });
};

/**
 * Starts the signalR client for the given MCell. Handles reconnection logic and incoming messages.
 * Returns a function that stops the client.
 * @param mCellId
 */
const startSignalR = (mCellId: string) => {
  let stopped = false;
  let failureCount = 0;
  let firstAttempt = true;
  let connection: HubConnection | undefined;
  let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

  const retry = (delay: number) => {
    if (!stopped) {
      retryTimeoutId = setTimeout(connect, delay);
    }
  };

  const connect = async () => {
    const isRetry = !firstAttempt;
    firstAttempt = false;
    connection = new HubConnectionBuilder()
      .withUrl(
        SIGNAL_R_ROOT_PATH +
          qs.stringify({ mCellId }, { addQueryPrefix: true }),
        {
          accessTokenFactory: async () => (await getAccessToken()) as string,
          skipNegotiation: false,
          transport:
            HttpTransportType.WebSockets |
            HttpTransportType.ServerSentEvents |
            HttpTransportType.LongPolling,
        },
      )
      .withAutomaticReconnect(RECONNECT_DELAYS_MS)
      .configureLogging(LogLevel.Information)
      .build();

    connection.onclose(() => {
      setConnectionState('disconnected');
      // restart when connection closes because sometimes it does not transition to reconnection state
      retry(1000);
    });
    connection.onreconnecting(() => setConnectionState('disconnected'));
    connection.onreconnected(() => {
      setConnectionState('connected');
      setConnectionFailed(false);
      // Pushes may have been missed while disconnected, so refetch everything on screen
      queryClient.invalidateQueries();
    });
    handlePushes(connection);

    try {
      await connection.start();
      failureCount = 0;
      setConnectionFailed(false);
      setConnectionState('connected');
      // Pushes may have been missed since the first attempt, as in onreconnected
      if (isRetry) {
        queryClient.invalidateQueries();
      }
    } catch {
      // If we fail to connect, we will only show an alert after a few failures
      failureCount++;
      if (failureCount === FAILURE_THRESHOLD) {
        setConnectionFailed(true);
      }
      retry(3000);
    }
  };

  connect();

  return () => {
    stopped = true;
    clearTimeout(retryTimeoutId);
    connection?.stop();
  };
};

export default startSignalR;
